import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

export type Translations = Record<string, Record<string, string>>;

interface I18nContextType {
  locale: string;
  t: (key: string) => string;
  setLocale: (locale: string) => void;
}

const I18nContext = createContext<I18nContextType | null>(null);

const DEFAULT_LOCALE = 'en';
const STORAGE_KEY = 'task_management_locale';

const getBrowserLocale = (): string => {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE;
  return (navigator.language || DEFAULT_LOCALE).toLowerCase();
};

const normalizeLocale = (
  locale: string,
  translations: Translations,
): string => {
  if (translations[locale]) return locale;
  if (locale.startsWith('zh')) return translations['zh-cn'] ? 'zh-cn' : 'zh';
  if (locale.startsWith('en')) return translations.en ? 'en' : DEFAULT_LOCALE;
  return DEFAULT_LOCALE;
};

export function createI18n(translations: Translations) {
  const getCurrentLocale = (): string => {
    if (typeof window !== 'undefined') {
      const persisted = window.localStorage.getItem(STORAGE_KEY);
      if (persisted)
        return normalizeLocale(persisted.toLowerCase(), translations);
    }
    return normalizeLocale(getBrowserLocale(), translations);
  };

  const createTranslatorByLocale = (locale: string) => {
    return (key: string): string => {
      const normalized = normalizeLocale(locale, translations);
      const messages = translations[normalized] || translations[DEFAULT_LOCALE];
      return messages?.[key] || key;
    };
  };

  return {
    translations,
    getCurrentLocale,
    createTranslatorByLocale,
  };
}

interface I18nProviderProps {
  children: ReactNode;
  translations: Translations;
}

export function I18nProvider({ children, translations }: I18nProviderProps) {
  const [locale, setLocale] = useState<string>(DEFAULT_LOCALE);

  useEffect(() => {
    const initialLocale = normalizeLocale(getBrowserLocale(), translations);
    const persisted =
      typeof window !== 'undefined'
        ? window.localStorage.getItem(STORAGE_KEY)
        : null;
    const nextLocale = normalizeLocale(
      (persisted || initialLocale).toLowerCase(),
      translations,
    );
    setLocale(nextLocale);
  }, [translations]);

  const updateLocale = (nextLocale: string) => {
    const normalized = normalizeLocale(nextLocale.toLowerCase(), translations);
    setLocale(normalized);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, normalized);
    }
  };

  const t = (key: string): string => {
    const messages = translations[locale] || translations[DEFAULT_LOCALE];
    return messages?.[key] || key;
  };

  return (
    <I18nContext.Provider value={{ locale, t, setLocale: updateLocale }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextType {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
}

export function createTranslator(
  translations: Translations,
  locale: string,
): (key: string) => string {
  return (key: string): string => {
    const normalized = normalizeLocale(locale.toLowerCase(), translations);
    const messages = translations[normalized] || translations[DEFAULT_LOCALE];
    return messages?.[key] || key;
  };
}
